import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, StatusBadge, Amount } from '@/components/ui';
import type { MaintenanceContract } from '../types/contracts.types';
import { getPackageType } from '@/utils/status';
import { formatDate } from '@/utils/format';
import { cn } from '@/utils/cn';

interface ContractsMobileListProps {
  contracts: MaintenanceContract[];
  onEdit: (contract: MaintenanceContract) => void;
  onDelete: (contract: MaintenanceContract) => void;
  type?: 'construction' | 'maintenance' | 'all';
}

export function ContractsMobileList({ contracts, type = 'maintenance' }: ContractsMobileListProps) {
  const navigate = useNavigate();
  const basePath = type === 'construction' ? '/construction/contracts' : '/maintenance/contracts';

  const filteredContracts = type === 'all'
    ? contracts
    : contracts.filter(c => c.contract_type === type);

  if (filteredContracts.length === 0) {
    return (
      <div className="py-12 bg-white rounded-xl border border-slate-100 shadow-sm mx-4">
        <div className="flex flex-col items-center justify-center text-center px-4">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
            <i className="ri-file-search-line text-3xl text-slate-300" />
          </div>
          <h3 className="text-sm font-bold text-slate-900 mb-1">Không tìm thấy hợp đồng</h3>
          <p className="text-xs text-slate-500 max-w-[200px]">
            Thử thay đổi bộ lọc hoặc từ khóa tìm kiếm để tìm thấy kết quả.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 px-4 pb-12 md:hidden">
      {filteredContracts.map((contract) => (
        <Card
          key={contract.id}
          className={cn("p-4 active:scale-[0.98] transition-transform")}
          onClick={() => navigate(`${basePath}/detail/${contract.id}`)}
        >
          <div className="flex justify-between items-start mb-3">
            <div className="flex flex-col">
              <span className="text-xs font-bold text-slate-400 tracking-widest">{contract.contract_code}</span>
              <h3 className="font-bold text-slate-900 leading-tight">{contract.contract_name}</h3>
            </div>
            <div className="flex flex-col items-end gap-1.5">
              <StatusBadge status={contract.status_code} type="contract" size="sm" />
              {type === 'all' && (
                <StatusBadge status={contract.contract_type} type="contract-type" size="sm" />
              )}
            </div>
          </div>

          <div className="space-y-2 mb-4">
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <i className="ri-building-line text-slate-400" />
              <span className="truncate">{contract.project?.name || 'Chưa gán dự án'}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <i className="ri-money-dollar-circle-line text-slate-400" />
              <span className="flex items-center gap-1">
                Giá trị: <Amount value={contract.contract_value} format="compact" suffix="VNĐ" className="font-bold" />
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <StatusBadge status={contract.package_type} type="package" size="sm" />
            </div>
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-slate-50">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <i className="ri-calendar-event-line" />
              <span>{formatDate(contract.start_date)} - {formatDate(contract.end_date)}</span>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
